import type { CSATSurvey } from 'src/pages/csat/slice/csatTypes';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useTranslate } from 'src/locales';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';

import { Iconify } from 'src/components/iconify';

import { CsatSourceBadge } from './csat-source-badge';
import { CsatSurveyAnalysisDetails } from './csat-survey-analysis-details';

type AgentAvatarProps = {
  profileUrl?: string | null;
  name?: string;
  sx?: any;
};

export function AgentAvatar({ profileUrl, name, sx }: AgentAvatarProps) {
  const [getMediaUrl] = useGetMediaUrlMutation();
  const [signedUrl, setSignedUrl] = useState<string>('');

  useEffect(() => {
    if (!profileUrl) {
      setSignedUrl('');
      return;
    }
    if (profileUrl.startsWith('http') || profileUrl.startsWith('data:')) {
      setSignedUrl(profileUrl);
      return;
    }
    getMediaUrl({ fileName: profileUrl })
      .unwrap()
      .then((res: any) => {
        if (typeof res === 'string' && res) {
          setSignedUrl(res);
        } else if (res?.data && typeof res.data === 'string') {
          setSignedUrl(res.data);
        } else if (res?.url && typeof res.url === 'string') {
          setSignedUrl(res.url);
        }
      })
      .catch((err) => console.error('Failed to get avatar url', err));
  }, [profileUrl, getMediaUrl]);

  return (
    <Avatar src={signedUrl || undefined} sx={sx}>
      {!signedUrl && (name ? name.charAt(0).toUpperCase() : 'A')}
    </Avatar>
  );
}

export type ParsedComplianceItem = {
  ruleName: string;
  status: 'Pass' | 'Fail' | 'N/A';
  note?: string;
};

export function parseComplianceFeedback(
  feedback?: string | null,
  metadata?: Record<string, any> | null
): {
  summary: string;
  keywords: string[];
  items: ParsedComplianceItem[];
  closingNote: string;
} {
  if (metadata?.aiEvaluation) {
    const aiEval = metadata.aiEvaluation;
    return {
      summary: aiEval.executiveSummary || aiEval.summary || feedback?.trim() || '',
      keywords: Array.isArray(aiEval.keywordsDetected) ? aiEval.keywordsDetected : [],
      items: Array.isArray(aiEval.complianceAudit) ? (aiEval.complianceAudit as ParsedComplianceItem[]) : [],
      closingNote: aiEval.closingNote || '',
    };
  }
  if (metadata?.complianceAudit && Array.isArray(metadata.complianceAudit)) {
    return {
      summary: metadata.executiveSummary || feedback?.trim() || '',
      keywords: Array.isArray(metadata.keywordsDetected) ? metadata.keywordsDetected : [],
      items: metadata.complianceAudit as ParsedComplianceItem[],
      closingNote: metadata.closingNote || '',
    };
  }

  if (!feedback)
    return {
      summary: '',
      keywords: [] as string[],
      items: [] as ParsedComplianceItem[],
      closingNote: '',
    };

  const text = feedback.trim();

  let keywords: string[] = [];
  const keywordMatch = text.match(/keywords\s+detected:\s*([^.]+)/i);
  if (keywordMatch) {
    keywords = keywordMatch[1]
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
  }

  const items: ParsedComplianceItem[] = [];
  let summary = '';
  let closingNote = '';

  // 1. Try standard colon-separated parenthesized list first
  const parts = text.split(/compliance\s*(?:checks?|failures?|audit|standards|status|review|summary|details)?\s*(?:include|are|is|status)?\s*:/i);
  if (parts.length >= 2) {
    const rawSummary = parts[0].trim();
    summary = rawSummary.replace(/keywords\s+detected:\s*[^.]+\.?/i, '').trim();
    const rest = parts.slice(1).join('Compliance:').trim();
    const isFailureList = /compliance\s+failures/i.test(text);

    const regex = /([^,().:;]+?)\s*\(([^)]+)\)/gi;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(rest)) !== null) {
      let ruleName = match[1].trim().replace(/^[,\s]+/, '').trim();
      ruleName = ruleName.replace(/^and\s+/i, '').trim();

      const noteRaw = match[2].trim();
      const noteLow = noteRaw.toLowerCase();

      let status: 'Pass' | 'Fail' | 'N/A' = isFailureList ? 'Fail' : 'Pass';

      if (
        noteLow.startsWith('pass') ||
        noteLow.startsWith('met') ||
        noteLow.includes('success') ||
        noteLow.startsWith('yes')
      ) {
        status = 'Pass';
      } else if (
        noteLow.includes('fail') ||
        noteLow.includes('miss') ||
        noteLow.includes('lack') ||
        noteLow.includes('not met') ||
        noteLow.includes('no attempt') ||
        noteLow.includes('no ownership') ||
        noteLow.includes('no information') ||
        noteLow.includes('not followed') ||
        noteLow.includes('no commitment') ||
        noteLow.startsWith('no ') ||
        noteLow.includes(' without ')
      ) {
        status = 'Fail';
      } else if (noteLow.includes('n/a') || noteLow.includes('not applicable')) {
        status = 'N/A';
      }

      const cleanNote = noteRaw.replace(/^(?:Pass|Fail|Met|Missing|Lacking|N\/A)[-\s:;]*/i, '').trim();

      if (ruleName && ruleName.length < 100) {
        items.push({ ruleName, status, note: cleanNote || noteRaw || undefined });
      }
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < rest.length) {
      const remainingText = rest.slice(lastIndex).replace(/^[,.\s]+/, '').trim();
      if (remainingText.length > 10) {
        closingNote = remainingText;
      }
    }
  }

  // 2. Fallback to smart natural language list parsing if no parenthesized items matched
  if (items.length === 0) {
    summary = text.replace(/keywords\s+detected:\s*[^.]+\.?/i, '').trim();

    // Look for lists like "Compliance failures include A, B, and C."
    const failuresRegex = /compliance\s+failures?\s*(?:include|are|is|consists?\s+of)\s+([^.]+)/i;
    const failuresMatch = text.match(failuresRegex);
    if (failuresMatch) {
      const listStr = failuresMatch[1];
      const parsedNames = listStr
        .split(/, | and /)
        .map((item) => item.trim())
        .filter((item) => item.length > 2 && !item.toLowerCase().startsWith('include') && item.toLowerCase() !== 'and');

      parsedNames.forEach((ruleName) => {
        let cleanName = ruleName.replace(/^[,\s]+/, '').trim();
        cleanName = cleanName.replace(/^and\s+/i, '').trim();
        if (cleanName && cleanName.length < 100) {
          items.push({
            ruleName: cleanName,
            status: 'Fail',
            note: 'FAILED COMPLIANCE STANDARD',
          });
        }
      });
    }

    // Look for lists like "Compliance passes include A, B, and C."
    const passesRegex = /compliance\s*(?:passes|successes|standards\s+met|met\s+standards?)\s*(?:include|are|is)\s+([^.]+)/i;
    const passesMatch = text.match(passesRegex);
    if (passesMatch) {
      const listStr = passesMatch[1];
      const parsedNames = listStr
        .split(/, | and /)
        .map((item) => item.trim())
        .filter((item) => item.length > 2 && !item.toLowerCase().startsWith('include') && item.toLowerCase() !== 'and');

      parsedNames.forEach((ruleName) => {
        let cleanName = ruleName.replace(/^[,\s]+/, '').trim();
        cleanName = cleanName.replace(/^and\s+/i, '').trim();
        if (cleanName && cleanName.length < 100) {
          items.push({
            ruleName: cleanName,
            status: 'Pass',
            note: 'MET COMPLIANCE STANDARD',
          });
        }
      });
    }

    // Adjust summary and closingNote if items were parsed successfully
    if (items.length > 0) {
      const sentenceRegex = /[^.!?]*compliance\s*(?:failures?|passes?|successes?|standards?|met)[^.!?]*[.!?]/gi;
      summary = text.replace(sentenceRegex, '').replace(/keywords\s+detected:\s*[^.]+\.?/i, '').trim();
      
      if (!summary) {
        summary = text.replace(/keywords\s+detected:\s*[^.]+\.?/i, '').trim();
      }

      const sentences = text.match(/[^.!?]+[.!?]/g) || [];
      if (sentences.length > 0) {
        const lastSentence = sentences[sentences.length - 1].trim();
        if (
          !/compliance/i.test(lastSentence) &&
          !/keywords\s+detected/i.test(lastSentence) &&
          lastSentence.length > 10
        ) {
          closingNote = lastSentence;
          summary = summary.replace(lastSentence, '').trim();
        }
      }
    }
  }

  return { summary, keywords, items, closingNote };
}

export type Props = {
  survey?: CSATSurvey | null;
  open: boolean;
  onClose: () => void;
};

export function CsatSurveyDetailsDialog({ survey, open, onClose }: Props) {
  const { t } = useTranslate('translation');

  if (!survey) return null;

  const { summary, keywords, items, closingNote } = parseComplianceFeedback(survey.feedback, survey.metadata);

  const metadata = survey.metadata ?? {};
  let channelRaw = String(metadata.channelType ?? metadata.channel ?? '').toUpperCase();
  if (survey.source === 'ai_voice') {
    channelRaw = 'VOICE';
  } else if (!channelRaw) {
    channelRaw = 'WEB';
  }
  const messageCount = metadata.messageCount != null ? Number(metadata.messageCount) : undefined;
  const transcriptLength =
    metadata.transcriptLength != null ? Number(metadata.transcriptLength) : undefined;
  const audioText =
    typeof metadata.audioText === 'string' && metadata.audioText.trim()
      ? metadata.audioText.trim()
      : '';
  const sttProviderLabel =
    typeof metadata.sttProvider === 'string' ? metadata.sttProvider : undefined;
  const sttLanguageLabel =
    typeof metadata.sttLanguage === 'string' ? metadata.sttLanguage : undefined;
  const confidence = metadata.confidence != null ? Number(metadata.confidence) : undefined;
  const isVoiceSurvey = survey.source === 'ai_voice';

  const channelIcons: Record<string, { icon: string; color: string; name: string }> = {
    WHATSAPP: { icon: 'mdi:whatsapp', color: '#25D366', name: 'WhatsApp' },
    INSTAGRAM: { icon: 'mdi:instagram', color: '#E4405F', name: 'Instagram' },
    EMAIL: { icon: 'solar:letter-bold', color: '#EA4335', name: 'Email' },
    CHAT: { icon: 'solar:chat-round-dots-bold', color: '#0066FF', name: 'Live Chat' },
    VOICE: { icon: 'solar:phone-calling-bold', color: '#34A853', name: 'Voice' },
    SMS: { icon: 'solar:smartphone-bold', color: '#FBBC05', name: 'SMS Messaging' },
  };
  const channelInfo = channelIcons[channelRaw] ?? {
    icon: 'solar:routing-3-bold',
    color: '#637381',
    name: channelRaw,
  };

  const sentiment = survey.sentimentAtClose ?? 'neutral';
  const sentimentColor =
    sentiment === 'positive' ? '#00A76F' : sentiment === 'negative' ? '#FF5630' : '#FFB800';

  const passCount = items.filter((i) => i.status === 'Pass').length;
  const failCount = items.filter((i) => i.status === 'Fail').length;
  const naCount = items.filter((i) => i.status === 'N/A').length;

  const agent = survey.agent;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Premium Dialog Header */}
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha('#00A76F', 0.12),
              color: '#00A76F',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="solar:chart-square-bold-duotone" width={24} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
            >
              {t('CSAT_SURVEY_REPORT') || 'AI Conversation Evaluation Report'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
              ID: {survey.conversationId}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Chip
            size="small"
            icon={<Iconify icon={channelInfo.icon} />}
            label={channelInfo.name}
            sx={{
              bgcolor: alpha(channelInfo.color, 0.12),
              color: channelInfo.color,
              fontWeight: 700,
              px: 0.5,
            }}
          />
          <CsatSourceBadge source={survey.source} />
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Iconify icon="mdi:close" width={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2.5, md: 4 }, mt: { xs: 2, md: 3 } }}>
        <Stack spacing={4}>
          {/* Evaluated Agent Section */}
          {agent && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: alpha('#919EAB', 0.04),
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <AgentAvatar
                  profileUrl={agent.profileUrl}
                  name={agent.name || agent.firstName}
                  sx={{
                    width: 56,
                    height: 56,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    {t('CSAT_EVALUATED_AGENT') || 'Evaluated Support Agent'}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
                  >
                    {agent.name ||
                      `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim() ||
                      'Support Agent'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {agent.email}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                size="small"
                icon={<Iconify icon="solar:shield-user-bold" />}
                label={agent.status ?? 'ACTIVE'}
                sx={{ bgcolor: alpha('#00A76F', 0.12), color: '#00A76F', fontWeight: 700 }}
              />
            </Paper>
          )}

          {/* Top Key Metrics Banner */}
          <Grid container spacing={2}>
            {/* 1. Rating Badge */}
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: alpha('#FFB800', 0.05),
                  borderColor: alpha('#FFB800', 0.25),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#FFB800', textTransform: 'uppercase', mb: 0.5 }}
                >
                  {t('CSAT_RATING') || 'Rating Score'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}
                  >
                    {survey.rating}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: 'text.disabled', fontWeight: 500, alignSelf: 'flex-end' }}
                  >
                    /5
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.25}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Iconify
                      key={star}
                      icon="solar:star-bold"
                      width={16}
                      color={star <= survey.rating ? '#FFB800' : alpha('#FFB800', 0.2)}
                    />
                  ))}
                </Stack>
              </Paper>
            </Grid>

            {/* 2. Sentiment Badge */}
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: alpha(sentimentColor, 0.05),
                  borderColor: alpha(sentimentColor, 0.25),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: sentimentColor,
                    textTransform: 'uppercase',
                    mb: 0.5,
                  }}
                >
                  {t('CSAT_SENTIMENT') || 'Sentiment Analysis'}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: sentimentColor,
                    textTransform: 'capitalize',
                    mb: 0.5,
                  }}
                >
                  {sentiment}
                </Typography>
                {survey.sentimentScore != null && (
                  <Chip
                    size="small"
                    label={`${(survey.sentimentScore * 100).toFixed(0)}% Score`}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      bgcolor: alpha(sentimentColor, 0.15),
                      color: sentimentColor,
                    }}
                  />
                )}
              </Paper>
            </Grid>

            {/* 3. AI Confidence */}
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: alpha('#0066FF', 0.05),
                  borderColor: alpha('#0066FF', 0.25),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#0066FF', textTransform: 'uppercase', mb: 0.5 }}
                >
                  {t('CSAT_AI_CONFIDENCE') || 'AI Precision'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Iconify icon="solar:cpu-bolt-bold-duotone" width={28} color="#0066FF" />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}
                  >
                    {confidence != null ? `${(confidence * 100).toFixed(0)}%` : '95%'}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  High Reliability
                </Typography>
              </Paper>
            </Grid>

            {/* 4. Interaction Volume */}
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: alpha('#8E33FF', 0.05),
                  borderColor: alpha('#8E33FF', 0.25),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#8E33FF', textTransform: 'uppercase', mb: 0.5 }}
                >
                  {isVoiceSurvey
                    ? t('CSAT_TRANSCRIPT_LENGTH') || 'Transcript Length'
                    : t('CSAT_INTERACTION_VOLUME') || 'Conversation Depth'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Iconify
                    icon={
                      isVoiceSurvey
                        ? 'solar:document-text-bold-duotone'
                        : 'solar:chat-round-line-bold-duotone'
                    }
                    width={28}
                    color="#8E33FF"
                  />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}
                  >
                    {isVoiceSurvey ? (transcriptLength ?? audioText.length) : (messageCount ?? 7)}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {isVoiceSurvey ? t('CSAT_TRANSCRIPT_CHARS') || 'Characters' : 'Total Messages'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* AI Detected High-Impact Keywords */}
          {keywords.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  mb: 1.5,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                {t('CSAT_FLAGGED_KEYWORDS') || 'AI Detected High-Impact Keywords'}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.25}>
                {keywords.map((kw, idx) => (
                  <Chip
                    key={idx}
                    icon={<Iconify icon="solar:flag-bold-duotone" />}
                    label={kw}
                    sx={{
                      bgcolor: alpha('#FF5630', 0.12),
                      color: '#FF5630',
                      fontWeight: 700,
                      px: 1,
                      py: 2.25,
                      borderRadius: 2,
                      fontSize: '0.9rem',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {isVoiceSurvey && (sttProviderLabel || sttLanguageLabel) && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {sttProviderLabel && (
                <Chip
                  size="small"
                  icon={<Iconify icon="solar:cpu-bolt-bold-duotone" width={16} />}
                  label={`${t('CSAT_STT_PROVIDER')}: ${sttProviderLabel}`}
                  sx={{ fontWeight: 600 }}
                />
              )}
              {sttLanguageLabel && (
                <Chip
                  size="small"
                  icon={<Iconify icon="solar:global-bold-duotone" width={16} />}
                  label={`${t('CSAT_STT_LANGUAGE')}: ${sttLanguageLabel}`}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          )}

          {isVoiceSurvey && audioText && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                {t('CSAT_VOICE_TRANSCRIPT') || 'Call Transcript (STT)'}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2.5,
                  bgcolor: 'background.neutral',
                  borderColor: 'divider',
                  maxHeight: 280,
                  overflowY: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                >
                  {audioText}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Executive Summary Narrative */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                mb: 1,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {t('CSAT_EXECUTIVE_SUMMARY') || 'AI Executive Summary'}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                borderColor: 'divider',
                boxShadow: (theme) => `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.03)}`,
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha('#00A76F', 0.12),
                    color: '#00A76F',
                    display: 'flex',
                    flexShrink: 0,
                  }}
                >
                  <Iconify icon="solar:document-text-bold-duotone" width={26} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.primary', lineHeight: 1.7, fontWeight: 500 }}
                >
                  {summary || 'No narrative summary provided.'}
                </Typography>
              </Stack>
            </Paper>
          </Box>

          {/* Compliance Breakdown Grid */}
          {items.length > 0 && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                  }}
                >
                  {t('CSAT_COMPLIANCE_BREAKDOWN') || 'Communication Standard & Compliance Audit'}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {passCount > 0 && (
                    <Chip
                      size="small"
                      icon={<Iconify icon="solar:check-circle-bold" />}
                      label={`${passCount} Pass`}
                      sx={{ bgcolor: alpha('#00A76F', 0.12), color: '#00A76F', fontWeight: 700 }}
                    />
                  )}
                  {failCount > 0 && (
                    <Chip
                      size="small"
                      icon={<Iconify icon="solar:close-circle-bold" />}
                      label={`${failCount} Fail`}
                      sx={{ bgcolor: alpha('#FF5630', 0.12), color: '#FF5630', fontWeight: 700 }}
                    />
                  )}
                  {naCount > 0 && (
                    <Chip
                      size="small"
                      label={`${naCount} N/A`}
                      sx={{ bgcolor: 'action.selected', color: 'text.secondary', fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Stack>

              <Grid container spacing={2}>
                {items.map((item, idx) => {
                  const isPass = item.status === 'Pass';
                  const isFail = item.status === 'Fail';

                  const statusColor = isPass ? '#00A76F' : isFail ? '#FF5630' : '#637381';
                  const statusBg = isPass
                    ? alpha('#00A76F', 0.06)
                    : isFail
                      ? alpha('#FF5630', 0.06)
                      : alpha('#637381', 0.06);
                  const iconName = isPass
                    ? 'solar:check-circle-bold-duotone'
                    : isFail
                      ? 'solar:close-circle-bold-duotone'
                      : 'solar:minus-circle-bold-duotone';

                  return (
                    <Grid key={idx} size={{ xs: 12, sm: 6 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: statusBg,
                          borderColor: isFail ? alpha('#FF5630', 0.4) : 'divider',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: statusColor,
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{ minWidth: 0, mr: 1 }}
                        >
                          <Box
                            sx={{ mt: 0.25, color: statusColor, display: 'flex', flexShrink: 0 }}
                          >
                            <Iconify icon={iconName} width={22} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                wordBreak: 'break-word',
                                mb: item.note ? 0.5 : 0,
                              }}
                            >
                              {item.ruleName}
                            </Typography>
                            {item.note && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isFail ? '#FF5630' : 'text.secondary',
                                  display: 'block',
                                  lineHeight: 1.4,
                                  fontWeight: isFail ? 700 : 500,
                                  textTransform: isFail ? 'uppercase' : 'none',
                                }}
                              >
                                {item.note}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Chip
                          size="small"
                          label={item.status}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            bgcolor: isPass
                              ? alpha('#00A76F', 0.15)
                              : isFail
                                ? alpha('#FF5630', 0.15)
                                : alpha('#637381', 0.15),
                            color: statusColor,
                            flexShrink: 0,
                          }}
                        />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Closing Note if any */}
          {closingNote && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: alpha('#FF5630', 0.05),
                borderColor: alpha('#FF5630', 0.3),
                display: 'flex',
                alignItems: 'center',
                spacing: 2,
              }}
            >
              <Box sx={{ color: '#FF5630', mr: 2, display: 'flex' }}>
                <Iconify icon="solar:danger-triangle-bold-duotone" width={28} />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: '#FF5630', fontWeight: 600, lineHeight: 1.5 }}
              >
                {closingNote}
              </Typography>
            </Paper>
          )}

          <CsatSurveyAnalysisDetails survey={survey} />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ fontWeight: 600, px: 3, py: 1, borderRadius: 2 }}
        >
          {t('CLOSE') || 'Close Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
